import React, { forwardRef, useEffect, useRef } from 'react';
import gaLogger from 'src/utils/ga-logger';
import Slider from 'react-slick';
import { css } from 'styled-components';
import Layout from 'src/components/common/theme/Layout';
import { useStores } from 'src/utils/useStore';
import { checkValue } from 'src/components/common/helper';
import { SlickCarouselProps } from '../typings';

export const SlickSlider = forwardRef((props: SlickCarouselProps, ref: React.Ref<Slider>) => {
  const { children, settings, gaTracking, exClass = '', afterChange, accessibilityData } = props;
  const {
    cpcmslitefeed: { cpCmsLitePageData, spotlightPageData },
  } = useStores();
  const sliderScopeRef = useRef(null);
  // TODO: Need to fix this
  const primaryColour = checkValue(cpCmsLitePageData)
    ? cpCmsLitePageData?.primaryColour
    : checkValue(spotlightPageData)
    ? spotlightPageData?.primaryColour
    : '';

  const onSlickChange = (currentSlide: number) => {
    if (checkValue(gaTracking)) {
      const { eventCategory, eventAction, eventLabel } = gaTracking;
      gaLogger.logGa(eventCategory, eventAction, eventLabel + currentSlide);
    }

    // Update aria-selected
    if (sliderScopeRef.current) {
      const dots = sliderScopeRef.current.querySelectorAll('.slick-dots li button');
      dots.forEach((dot) => dot.setAttribute('aria-selected', 'false'));

      const activeDot = dots[currentSlide];
      if (activeDot) {
        activeDot.setAttribute('aria-selected', 'true');
      }
    }
    
    if (afterChange) {
      afterChange(currentSlide);
    }
  };

  settings.afterChange = onSlickChange;

  useEffect(() => {
    if (sliderScopeRef.current && accessibilityData?.enabled) {
      if (accessibilityData?.dotsAriaLabelBuilder) {
        const dots = sliderScopeRef.current.querySelectorAll('.slick-dots button');
        dots.forEach((dot, index) => {
          dot.setAttribute('type', 'button');
          dot.setAttribute(
            'aria-label',
            accessibilityData.dotsAriaLabelBuilder(index + 1, accessibilityData.totalSlides),
          );
          dot.setAttribute('aria-selected', 'false');
        });
      }

      // Select the active dot
      const activeDot = sliderScopeRef.current.querySelector('.slick-dots .slick-active button');
      if (activeDot) {
        activeDot.setAttribute('aria-selected', 'true');
      }

      if (accessibilityData?.prevButtonAriaLabel) {
        const arrowPrev = sliderScopeRef.current.querySelector('.slick-arrow.slick-prev');
        arrowPrev?.setAttribute('aria-label', accessibilityData.prevButtonAriaLabel);
      }
      if (accessibilityData?.nextButtonAriaLabel) {
        const arrowNext = sliderScopeRef.current.querySelector('.slick-arrow.slick-next');
        arrowNext?.setAttribute('aria-label', accessibilityData.nextButtonAriaLabel);
      }
    }
  }, [
    sliderScopeRef.current,
    accessibilityData?.enabled,
    accessibilityData?.dotsAriaLabelBuilder,
    accessibilityData?.nextButtonAriaLabel,
    accessibilityData?.prevButtonAriaLabel,
  ]);

  const moduleCss = () =>
    `
            .slick-dots li button:before{
              color: #202843;
            }
            .slick-dots .slick-active button:before{
              ${primaryColour !== '' ? `color: ${primaryColour} !important;` : `color: #000 !important`}
            }                                       
          `;

  return (
    <>
      {accessibilityData?.enabled ? (
        <div ref={sliderScopeRef}>
          <Layout
            HtmlTag="section"
            css={css`
              ${moduleCss()}
            `}
            className={exClass}
          >
            <Slider {...settings} ref={ref}>
              {children}
            </Slider>
          </Layout>
        </div>
      ) : (
        <Layout
          HtmlTag="section"
          css={css`
            ${moduleCss()}
          `}
          className={exClass}
        >
          <Slider {...settings} ref={ref}>
            {children}
          </Slider>
        </Layout>
      )}
    </>
  );
});

SlickSlider.displayName = 'SlickSlider';
